import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const operationLabels = { AWAITING_UPLOAD: "Da caricare", AWAITING_READER_START: "Da avviare", READING: "In lettura", HUMAN_REVIEW_REQUIRED: "Dati da verificare", READER_FAILED: "Lettura interrotta" };

export function RFQList() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/rfqs").then(({ data }) => setList(data)); }, []);
  return (
    <div className="p-4 sm:p-8" data-testid="rfqs-page">
      <div className="flex flex-wrap gap-3 items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Pipeline</div>
          <h1 className="text-3xl font-display font-bold mt-1">Richieste di offerta</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/rfqs/import" className="border border-border bg-white px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-md shadow-sm hover:bg-muted" data-testid="btn-import-rfq-excel">Importa da Excel</Link>
          <Link to="/rfqs/new" className="bg-primary text-primary-foreground px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-md shadow-sm hover:bg-[#E0C27A] border border-[#B5914B]/60" data-testid="btn-new-rfq-list">+ Nuovo preventivo</Link>
        </div>
      </div>
      <div className="border border-border bg-white">
        <table className="dense-table w-full">
          <thead>
            <tr><th>Riferimento</th><th>Cliente</th><th>Descrizione</th><th>Lotto · Q.tà</th><th>Stato</th><th>Affidabilità</th><th>Data richiesta</th></tr>
          </thead>
          <tbody>
            {list.map(r => (
              <tr key={r.id} data-testid={`rfq-row-${r.id}`}>
                <td className="font-mono"><Link to={`/rfqs/${r.id}`} className="hover:underline link-tech font-semibold">{r.reference}</Link></td>
                <td>{r.customer_name}</td>
                <td className="max-w-md truncate">{r.description}</td>
                <td className="text-xs" data-testid={`rfq-lot-${r.id}`}>{r.import_batch ? <>{r.import_batch.lot_reference || "Excel"}{r.quantities?.length ? ` · ${r.quantities[0]} pz` : " · q.tà non indicata"}</> : "—"}</td>
                <td><span className="text-xs" data-testid={`rfq-status-${r.id}`}>{operationLabels[r.operational_stage] || r.status}</span></td>
                <td><span className="text-xs" data-testid={`rfq-review-${r.id}`}>{r.operational_flow ? "Da verificare sul risultato" : r.reliability || "Da verificare"}</span></td>
                <td className="text-xs text-muted-foreground">{(r.requested_date || "").slice(0, 10)}</td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-muted-foreground text-sm" data-testid="rfqs-empty">Nessuna richiesta operativa. <Link className="underline" data-testid="rfqs-empty-new-quote" to="/rfqs/new">Carica il primo PDF</Link> oppure <Link className="underline" data-testid="rfqs-empty-import" to="/rfqs/import">importa un elenco Excel</Link></td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function NewRFQ() {
  const [customers, setCustomers] = useState([]);
  const [f, setF] = useState({ customer_id: "", reference: "", description: "", quantities_str: "1,10,50", requested_date: "" });
  const navigate = useNavigate();
  useEffect(() => { api.get("/customers").then(({ data }) => setCustomers(data)); }, []);

  const save = async (e) => {
    e.preventDefault();
    const quantities = f.quantities_str.split(",").map(x => parseInt(x.trim())).filter(x => x > 0);
    try {
      const { data } = await api.post("/rfqs", { ...f, quantities });
      toast.success("Richiesta creata");
      navigate(`/rfqs/${data.id}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="p-8 max-w-2xl" data-testid="new-rfq-page">
      <h1 className="text-3xl font-display font-bold mb-6">Nuova richiesta di offerta</h1>
      <form onSubmit={save} className="space-y-4 bg-white border border-border p-6">
        <div>
          <Label>Cliente</Label>
          <Select value={f.customer_id} onValueChange={(v) => setF({ ...f, customer_id: v })}>
            <SelectTrigger data-testid="rfq-customer"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
            <SelectContent>
              {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Riferimento (opz.)</Label><Input data-testid="rfq-reference" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="es. Codice cliente" /></div>
          <div><Label>Data richiesta</Label><Input data-testid="rfq-date" type="date" value={f.requested_date} onChange={(e) => setF({ ...f, requested_date: e.target.value })} /></div>
        </div>
        <div><Label>Descrizione</Label><Input data-testid="rfq-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
        <div><Label>Quantità (separate da virgola)</Label><Input data-testid="rfq-qty" value={f.quantities_str} onChange={(e) => setF({ ...f, quantities_str: e.target.value })} /></div>
        <Button type="submit" data-testid="rfq-save" className="w-full">Crea richiesta</Button>
      </form>
    </div>
  );
}
